import React, { useState, useMemo } from 'react';
import { ServiceOffering, Language, CreateExchangeData } from '@/types';
import { ServiceCard } from './ServiceCard';
import { Modal, Button, Alert, Loading } from '@/components/common';
import { getTranslation } from '@/constants';
import { useExchanges } from '@/hooks';
import { useAuth } from '@/context/AuthContext';

interface ServiceRequestListProps {
  services: ServiceOffering[];
  language: Language;
  loading?: boolean;
}

export const ServiceRequestList: React.FC<ServiceRequestListProps> = ({
  services,
  language,
  loading = false,
}) => {
  const { user, userProfile } = useAuth();
  const { requestService, exchanges } = useExchanges(user?.uid);
  const t = getTranslation(language);

  const [selectedService, setSelectedService] = useState<ServiceOffering | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newlyRequestedServices, setNewlyRequestedServices] = useState<Set<string>>(new Set());

  // Build a set of service offering IDs that the user has already requested
  // This persists across refreshes since it comes from the database
  const requestedServiceOfferingIds = useMemo(() => {
    const offeringIds = new Set<string>();
    exchanges.forEach((exchange) => {
      // If the current user is the requester, mark this specific service offering as requested
      if (exchange.requesterId === user?.uid && exchange.serviceOfferingId) {
        offeringIds.add(exchange.serviceOfferingId);
      }
    });
    return offeringIds;
  }, [exchanges, user?.uid]);

  // Check if a specific service offering has already been requested (from DB or newly requested in this session)
  const isServiceRequested = (service: ServiceOffering): boolean => {
    return requestedServiceOfferingIds.has(service.id) || newlyRequestedServices.has(service.id);
  };

  const handleRequestClick = (service: ServiceOffering) => {
    setSelectedService(service);
    setError(null);
    setSuccess(false);
  };

  const handleConfirmRequest = async () => {
    if (!selectedService || !user || !userProfile) return;

    try {
      setRequesting(true);
      setError(null);

      const exchangeData: CreateExchangeData = {
        serviceOfferingId: selectedService.id,
        providerId: selectedService.userId,
        providerName: selectedService.userName,
        providerEmail: selectedService.userEmail,
        providerPostalCode: selectedService.userPostalCode,
        requesterId: user.uid,
        requesterName: userProfile.displayName || userProfile.email,
        requesterEmail: userProfile.email,
        requesterPostalCode: userProfile.postalCode,
        serviceTypes: selectedService.serviceTypes,
        paymentMethod: selectedService.paymentPreferences[0],
        providerAvailability: selectedService.availability,
      };

      await requestService(exchangeData);
      setSuccess(true);

      // Mark this service as newly requested in this session
      setNewlyRequestedServices((prev) => new Set(prev).add(selectedService.id));

      setTimeout(() => {
        setSelectedService(null);
        setSuccess(false);
      }, 1500);
    } catch (err) {
      setError('Failed to send request. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return <Loading text={t.loading} />;
  }

  if (services.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t.noServices}</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((service) => (
          <ServiceCard
            key={service.id}
            service={service}
            language={language}
            onRequest={handleRequestClick}
            isRequested={isServiceRequested(service)}
          />
        ))}
      </div>

      {/* Request Confirmation Modal */}
      <Modal
        isOpen={selectedService !== null}
        onClose={() => !requesting && setSelectedService(null)}
        title={t.requestBtn}
        size="md"
      >
        {success ? (
          <Alert variant="success">{t.requestSent}</Alert>
        ) : (
          <>
            {error && (
              <Alert variant="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <div className="space-y-4">
              <p className="text-gray-700">
                {language === 'en'
                  ? `Send a service request to ${selectedService?.userName}?`
                  : `Envoyer une demande de service à ${selectedService?.userName}?`}
              </p>

              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => setSelectedService(null)}
                  disabled={requesting}
                >
                  {t.cancel}
                </Button>
                <Button fullWidth onClick={handleConfirmRequest} loading={requesting}>
                  {t.confirm}
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};
